import { useCallback, useEffect } from 'react';

import { MapCameraChangedEvent, useMap } from '@vis.gl/react-google-maps';

import { _useMapContext } from './Map.context';

/** returns the map object for the current map context */
const useLocalMap = (id: string) => useMap(id);

/**
 * Provides access to the nearest map Context Provider
 * Also returns the id of the map context with the useMap function
 */
const useMapContext = () => {
  const context = _useMapContext();

  return { ...context, useMap: () => useLocalMap(context.id) };
};

type MapEvenTypes = 'bounds_changed' | 'center_changed';

type UseMapEvent = {
  mapEvent: MapEvenTypes;
  cb: (event?: MapCameraChangedEvent) => void;
};

const useMapEvent = ({ mapEvent, cb }: UseMapEvent) => {
  const { useMap } = useMapContext();
  const map = useMap();
  useEffect(() => {
    if (!map) return;

    const remove = map?.addListener(mapEvent, cb);
    return () => {
      remove.remove();
    };
  }, [cb, map, mapEvent]);
};

/**
 * A wrapper around @vis.gl/react-google-maps useMap hook that provides additional functionality
 * This also uses the maps context to get the map object
 *
 */
const _useMap = () => {
  const { useMap } = useMapContext();
  const map = useMap();

  const getMapCenter = useCallback(() => {
    if (!map) return;
    const center = map.getCenter();
    if (!center) return;
    return {
      lat: center.lat(),
      lng: center.lng(),
    };
  }, [map]);

  const setMapCenter = useCallback(
    (latLong: { lat: number; lng: number }) => {
      if (map && latLong) {
        const { lat, lng } = latLong;
        map.panTo({ lat, lng });
      }
    },
    [map]
  );

  const handleZoomUpdate = useCallback(
    (increment: boolean) => {
      const zoom = map?.getZoom();
      if (!zoom || !map) return;
      const newZoom = increment ? zoom + 1 : zoom - 1;
      map.setZoom(newZoom);
    },
    [map]
  );

  return {
    getMapCenter,
    setMapCenter,
    handleZoomUpdate,
    useMapContext,
    useMapEvent,
    map,
  };
};

export { _useMap as useMap, useMapContext };
